import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import BottomNavigation from './components/BottomNavigation/BottomNavigation';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import { useDeviceDetection } from './hooks/useDeviceDetection';
import SEO from './components/SEO/SEO';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import PWAInstallPrompt from './components/PWAInstallPrompt/PWAInstallPrompt';
import ConnectionStatus from './components/ConnectionStatus/ConnectionStatus';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import AnalyticsConsent from './components/AnalyticsConsent/AnalyticsConsent';
import { clearBanFlag, logout } from './store/slices/authSlice';
import { AlertCircle, X } from 'lucide-react';
import analyticsService from './services/analyticsService';
import {
  LazyHomePage2,
  LazyMapPage,
  LazyFeedPage,
  LazyProfilePage,
  LazyGroupsPage,
  LazyAdminPanel,
  LazyRouteDetailPage,
  LazyVideoDetailPage,
  LazyLoginPage,
  LazyRegisterPage,
  LazyContactUsPage,
  LazyPrivacyPolicyPage,
  LazyTermsOfServicePage,
  LazyCookiePolicyPage,
  LazyForgotPasswordPage,
  LazyResetPasswordPage,
  LazyVerifyEmailPage,
  LazyAboutPage,
  LazyRouteDrawingGuidePage,
  LazyFAQPage,
  LazyForCreatorsPage
} from './components/PerformanceOptimization/PerformanceOptimization';
import HomePageSkeleton from './pages/HomePage/HomePageSkeleton';
import 'react-toastify/dist/ReactToastify.css';
import { useWebSocket } from './hooks/useWebSocket';

// Lazy load компонентов
const PreRegistrationTour = React.lazy(() => import('./components/Onboarding/PreRegistrationTour'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage/NotFoundPage'));

// Компонент для инициализации WebSocket
const WebSocketInitializer = () => {
  useWebSocket(); // Инициализируем WebSocket
  return null;
};

// Компонент для показа модального окна о бане
const BanModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isBanned = useSelector((state) => state.auth.isBanned);
  const [mounted, setMounted] = React.useState(false);

  // Отслеживаем монтирование, чтобы модальное окно показывалось сразу
  React.useEffect(() => {
    if (isBanned) {
      setMounted(true);
      // Дополнительно проверяем sessionStorage для надежности
      if (sessionStorage.getItem('isBanned') !== 'true') {
        sessionStorage.setItem('isBanned', 'true');
      }
    }
  }, [isBanned]);
  
  // Также проверяем sessionStorage при монтировании компонента
  React.useEffect(() => {
    if (sessionStorage.getItem('isBanned') === 'true') {
      setMounted(true);
    }
  }, []);

  const handleClose = () => {
    setMounted(false);
    dispatch(clearBanFlag());
    dispatch(logout());
    navigate('/login');
  };

  // Блокируем скролл фона когда модальное окно открыто
  React.useEffect(() => {
    if (isBanned || mounted) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isBanned, mounted]);

  // Показываем модальное окно, если забанен или если оно было показано (mounted)
  if (!isBanned && !mounted) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="flex items-center space-x-3 flex-1">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Учетная запись заблокирована
              </h3>
            </div>
          </div>
          <div className="mb-6">
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              Ваша учетная запись была заблокирована администратором.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Для получения дополнительной информации или разблокировки аккаунта обратитесь к администратору платформы.
            </p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Понятно
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Компонент для отслеживания навигации
const AnalyticsTracker = () => {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    analyticsService.init();
    analyticsService.initYandexFromSettings();
  }, []);

  useEffect(() => {
    // Отслеживание переходов между страницами
    analyticsService.trackPageView(
      location.pathname + location.search,
      document.title
    );
  }, [location]);

  useEffect(() => {
    // Установка User ID при входе пользователя
    if (user?.id) {
      analyticsService.setUserId(user.id.toString());
      analyticsService.setUserProperties({
        user_type: user.role || 'user',
        user_email: user.email || ''
      });
    }
  }, [user]);

  return null;
};

function App() {
  const device = useDeviceDetection();

  return (
    <div className="App min-h-screen bg-gray-50">
      <SEO />
      <AnalyticsTracker />
      <WebSocketInitializer />
      <BanModal />
      <ConnectionStatus />
      <AnalyticsConsent />
      <Suspense fallback={null}>
        <PreRegistrationTour />
      </Suspense>
      <Header />
      <ScrollToTop />
      <main className="flex-grow pb-16 md:pb-0">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Suspense fallback={<HomePageSkeleton />}><LazyHomePage2 /></Suspense>} />
                        <Route path="/home2" element={<Navigate to="/" replace />} />
            <Route path="/home-old" element={<Navigate to="/" replace />} />
            {/* Редирект с /index.html на главную (после fallback-редиректа с бэкенда пользователь не должен видеть 404) */}
            <Route path="/index.html" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<LazyLoginPage />} />
            <Route path="/register" element={<LazyRegisterPage />} />
            <Route path="/forgot-password" element={<LazyForgotPasswordPage />} />
            <Route path="/reset-password" element={<LazyResetPasswordPage />} />
            <Route path="/contact-us" element={<LazyContactUsPage />} />
            <Route path="/privacy" element={<LazyPrivacyPolicyPage />} />
            <Route path="/terms" element={<LazyTermsOfServicePage />} />
            <Route path="/cookies" element={<LazyCookiePolicyPage />} />
            <Route path="/about" element={<LazyAboutPage />} />
            <Route path="/for-creators" element={<LazyForCreatorsPage />} />
            <Route path="/guide" element={<LazyRouteDrawingGuidePage />} />
            <Route path="/faq" element={<LazyFAQPage />} />
            <Route path="/verify-email" element={<LazyVerifyEmailPage />} />
            <Route path="/map" element={<LazyMapPage />} />
            <Route path="/feed" element={
              <Suspense fallback={<LoadingSpinner />}>
                <LazyFeedPage />
              </Suspense>
            } />
            <Route path="/route/:id/:lang" element={<LazyRouteDetailPage />} />
            <Route path="/route/:id" element={<LazyRouteDetailPage />} />
            <Route path="/video/:id/:lang" element={<LazyVideoDetailPage />} />
            <Route path="/video/:id" element={<LazyVideoDetailPage />} />
            
            {/* Protected routes */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <LazyProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/profile/:id/:lang" element={
              <ProtectedRoute>
                <LazyProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/profile/:id" element={
              <ProtectedRoute>
                <LazyProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/groups" element={
              <ProtectedRoute>
                <LazyGroupsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute adminOnly={true}>
                <LazyAdminPanel />
              </ProtectedRoute>
            } />
            
            {/* 404 - Catch all other routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      {/* Bottom Navigation - на мобильных устройствах (в обеих ориентациях) */}
      {device.isMobile && <BottomNavigation key={`bottom-nav-${device.isLandscape}`} />}
      <PWAInstallPrompt />
      <ToastContainer
        position={device.isMobile ? "top-center" : "top-right"}
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        className={device.isMobile ? "mobile-toast-container" : ""}
        style={device.isMobile ? { 
          width: '90vw',
          maxWidth: '400px',
          maxHeight: '80vh',
          overflowY: 'auto'
        } : {}}
      />

    </div>
  );
}

export default App;